// src/app/modules/Technical/pages/Machinery/EquipmentModal.tsx
import React, { FC, useEffect, useMemo, useState } from 'react'
import type { EquipmentDto } from '../../core/_models'
import type { Vessel } from '../../../Management/core/_models'
import { useAuth } from '../../../auth'
import { getVesselList, getCompanyList, getCompanyAdminList } from '../../../Management/core/_requests'
import { createVesselLocation, getVesselLocations, VesselLocationDto } from '../../core/_requests'
import { getAccessibleVessels, isVesselAssignedUserOnly } from '../../core/technicalScopeUtils'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

type CompanyGroup = { id: number; name: string }
type Subcompany = { id: number; name: string; companyId: number }

interface EquipmentModalProps {
  visible: boolean
  vesselId?: number
  editing?: EquipmentDto
  onClose: () => void
  onSubmit: (data: EquipmentDto) => void
  allEquipment?: EquipmentDto[]   
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const CUSTOM_NAME_OPTION = '__CUSTOM_NAME__'
const CUSTOM_CODE_OPTION = '__CUSTOM_CODE__'

const EQUIPMENT_NAME_OPTIONS: { group: string; items: string[] }[] = [
  { group: 'Main Engine', items: ['Main Engine 1', 'Main Engine 2', 'Main Engine 3'] },
  { group: 'Auxiliary Engine', items: ['Auxiliary Engine 1', 'Auxiliary Engine 2', 'Auxiliary Engine 3'] },
  { group: 'Boiler', items: ['Boiler 1', 'Boiler 2', 'Boiler 3'] },
  { group: 'Special Systems', items: ['IGG', 'IGS', 'EGCS'] },
  { group: 'Purifier - FO', items: ['Purifier-FO 1', 'Purifier-FO 2', 'Purifier-FO 3'] },
  { group: 'Purifier - LO', items: ['Purifier-LO 1', 'Purifier-LO 2', 'Purifier-LO 3'] },
  {
    group: 'Others',
    items: [
      'Other Machinery',
      'Deck',
      'MARPOL',
      'LSA',
      'FFA',
      'Cargo Gear',
      'Cargo Pumps',
      'MLC',
      'Other Equipment',
      'Other (Custom)',
    ],
  },
]

const EQUIPMENT_CODE_OPTIONS: { value: string; label: string }[] = [
  { value: 'ENGDK', label: 'ENGDK - Engine Deck' },
  { value: 'CREQ', label: 'CREQ - Critical Equipment' },
  { value: 'MARPOL', label: 'MARPOL' },
  { value: 'LSA', label: 'LSA - Life Saving Appliances' },
  { value: 'FFA', label: 'FFA - Fire Fighting Appliances' },
  { value: 'CGGR', label: 'CGGR - Cargo Gear' },
  { value: 'CGOP', label: 'CGOP - Cargo Pumps' },
  { value: 'MLC', label: 'MLC' },
  { value: 'NAV', label: 'NAV - Navigation' },
  { value: 'OTHERS', label: 'OTHERS - Other Equipment' },
  { value: CUSTOM_CODE_OPTION, label: 'Other (Custom)' },
]

const LOCATION_CODE_PATTERN = /^\d{2}\.\d{2}\.\d{2}$/

const SHORT_FORM_MAP: Record<string, string> = {
  'MAIN ENGINE 1': 'MENG1',
  'MAIN ENGINE 2': 'MENG2',
  'MAIN ENGINE 3': 'MENG3',
  'AUXILIARY ENGINE 1': 'AENG1',
  'AUXILIARY ENGINE 2': 'AENG2',
  'AUXILIARY ENGINE 3': 'AENG3',
  'BOILER 1': 'BLR1',
  'BOILER 2': 'BLR2',
  'BOILER 3': 'BLR3',
  'IGG': 'IGG',
  'IGS': 'IGS',
  'EGCS': 'EGCS',
  'PURIFIER-FO 1': 'PFO1',
  'PURIFIER-FO 2': 'PFO2',
  'PURIFIER-FO 3': 'PFO3',
  'PURIFIER-LO 1': 'PLO1',
  'PURIFIER-LO 2': 'PLO2',
  'PURIFIER-LO 3': 'PLO3',
  'DECK': 'DECK',
  'MARPOL': 'MPL',
  'LSA': 'LSA',
  'FFA': 'FFA',
  'CARGO GEAR': 'CG',
  'CARGO PUMPS': 'CP',
  'MLC': 'MLC',
  'OTHER MACHINERY': 'OM',
  'OTHER EQUIPMENT': 'OE',
}

const LABEL_HELP_SUBHEAD =
  'Sub Head / Details (for MARPOL, LSA, FFA, Other Machinery)'

export const EquipmentModal: FC<EquipmentModalProps> = ({
  visible,
  vesselId: initialVesselId,
  editing,
  onClose,
  onSubmit,
  allEquipment,
}) => {
  const { currentUser, isSuperAdmin } = useAuth()
  const isVesselOnlyUser = isVesselAssignedUserOnly(currentUser, isSuperAdmin)

  const [vessels, setVessels] = useState<Vessel[]>([])
  const [vesselsScoped, setVesselsScoped] = useState<Vessel[]>([])
  const [companies, setCompanies] = useState<CompanyGroup[]>([])
  const [subcompanies, setSubcompanies] = useState<Subcompany[]>([])
  const [companyId, setCompanyId] = useState<string>('')
  const [subcompanyId, setSubcompanyId] = useState<string>('')
  const [vesselLocations, setVesselLocations] = useState<VesselLocationDto[]>([])
  const [vesselLocationCode, setVesselLocationCode] = useState<string>('')

  const [formData, setFormData] = useState<EquipmentDto>({
    vesselId: initialVesselId || editing?.vesselId || 0,
    name: editing?.name || '',
    code: editing?.code || '',
    shortForm: editing?.shortForm || '',
    criticality: editing?.criticality || false,
    subHead: editing?.subHead || '',
    maker: editing?.maker || '',
    model: editing?.model || '',
    serialNumber: editing?.serialNumber || '',
    installationDate: editing?.installationDate || '',
    location: editing?.location || '',
    equipmentParticulars: editing?.equipmentParticulars || '',
    isClassCritical: editing?.isClassCritical || false,
    isSurveyRelevant: editing?.isSurveyRelevant || false,
    vesselLocationId: editing?.vesselLocationId || undefined,
    equipmentFunctionDescription: editing?.equipmentFunctionDescription || '',
    vesselLocationComments: editing?.vesselLocationComments || '',
    instanceOfEquipmentId: editing?.instanceOfEquipmentId || undefined,
  })

  const [selectedEquipmentName, setSelectedEquipmentName] = useState<string>('')
  const [selectedEquipmentCode, setSelectedEquipmentCode] = useState<string>('')
  const [customEquipmentName, setCustomEquipmentName] = useState<string>('')
  const [customEquipmentCode, setCustomEquipmentCode] = useState<string>('')
  const [instanceOfCode, setInstanceOfCode] = useState<string>('')
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const selectedVesselName = useMemo(() => {
    if (editing?.vesselName) return editing.vesselName
    if (isVesselOnlyUser && currentUser?.vessel?.fleet_name) return currentUser.vessel.fleet_name
    const vId = formData.vesselId || initialVesselId
    const all = vesselsScoped.length > 0 ? vesselsScoped : vessels
    const match = all.find(v => v.id === vId)
    return (match as any)?.fleet_name || (match as any)?.name || (vId ? `Vessel ${vId}` : '')
  }, [
    editing?.vesselName,
    isVesselOnlyUser,
    currentUser?.vessel?.fleet_name,
    formData.vesselId,
    initialVesselId,
    vesselsScoped,
    vessels,
  ])

const existingNamesSet = useMemo(() => {
  if (!formData.vesselId || !allEquipment) return new Set<string>()

  const normalized = allEquipment
    .filter(e =>
      e.vesselId === formData.vesselId &&
      (!editing || e.id !== editing.id) // allow the name of the record we're editing
    )
    .map(e => e.name.toUpperCase().trim())

  return new Set(normalized)
}, [allEquipment, formData.vesselId, editing?.id])


  useEffect(() => {
    if (!visible) return
    if (editing?.vesselId) {
      setFormData(prev => ({ ...prev, vesselId: editing.vesselId || prev.vesselId }))
    } else if (initialVesselId) {
      setFormData(prev => ({ ...prev, vesselId: initialVesselId }))
    }

    const loadLookups = async () => {
      try {
        const [groupsRaw, adminsRaw, vesselList] = await Promise.all([
          getCompanyAdminList?.().catch(() => []),
          getCompanyList?.().catch(() => []),
          getVesselList().catch(() => [] as Vessel[]),
        ])

        const groups: CompanyGroup[] = Array.isArray(groupsRaw)
          ? groupsRaw.map((g: any) => ({
              id: Number(g.id),
              name: g.name ?? g.companyGroupAdminName ?? `Group #${g.id}`,
            }))
          : []

        const admins: Subcompany[] = Array.isArray(adminsRaw)
          ? adminsRaw.map((a: any) => ({
              id: Number(a.id),
              name: a.name ?? a.companyAdminName ?? `Company #${a.id}`,
              companyId: Number(
                a.cgaid?.id ??
                a.cga?.id ??
                a.companyGroupAdminId ??
                a.companyGroupId
              ),
            }))
          : []

        setCompanies(groups.filter(g => Number.isFinite(g.id)))
        setSubcompanies(admins.filter(a => Number.isFinite(a.id) && Number.isFinite(a.companyId)))

        const vesselsForUser = getAccessibleVessels(vesselList, currentUser, isSuperAdmin)

        setVessels(vesselList)
        setVesselsScoped(vesselsForUser)

        const assignedVesselId = currentUser?.vessel?.id
        if (isVesselOnlyUser && assignedVesselId) {
          setFormData(prev => ({ ...prev, vesselId: assignedVesselId }))
        } else if (!isVesselOnlyUser && !formData.vesselId && !initialVesselId && vesselsForUser.length > 0) {
          setFormData(prev => ({ ...prev, vesselId: vesselsForUser[0].id }))
        }
      } catch (error) {
        console.error('Error loading lookups for equipment modal:', error)
      }
    }

    loadLookups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  // Load vessel locations when vesselId changes
  useEffect(() => {
    const loadVesselLocations = async () => {
      if (!formData.vesselId || formData.vesselId === 0) {
        setVesselLocations([])
        setVesselLocationCode('')
        return
      }
      try {
        const locations = await getVesselLocations(formData.vesselId)
        setVesselLocations(locations)
      } catch (error) {
        console.error('Error loading vessel locations:', error)
        setVesselLocations([])
      }
    }
    loadVesselLocations()
  }, [formData.vesselId])

  useEffect(() => {
    if (editing) {
      const allNames = EQUIPMENT_NAME_OPTIONS.flatMap(g => g.items)
      const matched = allNames.find(item => item.toLowerCase() === editing.name.toLowerCase())
      const isCustomName = !matched
      setSelectedEquipmentName(isCustomName ? CUSTOM_NAME_OPTION : matched || '')
      setCustomEquipmentName(isCustomName ? editing.name : '')

      const codeMatch = EQUIPMENT_CODE_OPTIONS.find(opt => opt.value === editing.code)
      const isCustomCode = !codeMatch
      if (isCustomName) {
        setSelectedEquipmentCode(CUSTOM_CODE_OPTION)
        setCustomEquipmentCode(editing.code || '')
      } else {
        setSelectedEquipmentCode(isCustomCode ? CUSTOM_CODE_OPTION : (editing.code || ''))
        setCustomEquipmentCode(isCustomCode ? (editing.code || '') : '')
      }

      if (editing.instanceOfEquipmentId && allEquipment?.length) {
        const match = allEquipment.find(e => e.id === editing.instanceOfEquipmentId)
        setInstanceOfCode(match?.code || '')
      } else {
        setInstanceOfCode('')
      }
    } else {
      setSelectedEquipmentName('')
      setSelectedEquipmentCode('')
      setCustomEquipmentName('')
      setCustomEquipmentCode('')
      setVesselLocationCode('')
      setInstanceOfCode('')
    }
    setErrors({})
  }, [editing])

  useEffect(() => {
    if (!editing?.vesselLocationId) return
    const match = vesselLocations.find(l => l.id === editing.vesselLocationId)
    if (match?.code) {
      setVesselLocationCode(match.code)
    }
  }, [editing?.vesselLocationId, vesselLocations])

  const vesselsForModal = useMemo(() => {
    let list = vesselsScoped
    if (companyId) {
      const cid = Number(companyId)
      list = list.filter((v: any) => Number(v.companyGroupAdmin?.id ?? v.companyGroupId) === cid)
    }
    if (subcompanyId) {
      const scid = Number(subcompanyId)
      list = list.filter((v: any) => Number(v.companyAdmin?.id ?? v.companyId) === scid)
    }
    return list
  }, [vesselsScoped, companyId, subcompanyId])

  const subcompaniesForCompany = useMemo(() => {
    if (!companyId) return []
    const cid = Number(companyId)
    return subcompanies.filter(sc => sc.companyId === cid)
  }, [subcompanies, companyId])

  const generateShortForm = (name: string): string => {
    if (!name) return ''
    const key = name.toUpperCase().trim()
    if (SHORT_FORM_MAP[key]) return SHORT_FORM_MAP[key]

    const words = key.split(/\s+/)
    let shortForm = ''
    for (const w of words) {
      if (/^\d+$/.test(w)) {
        shortForm += w
      } else if (w.length > 0) {
        shortForm += w[0]
      }
    }
    return shortForm
  }

  const deriveDefaultCodeFromName = (value: string): string => {
    const upper = value.toUpperCase()
    if (
      upper.startsWith('MAIN ENGINE') ||
      upper.startsWith('AUXILIARY ENGINE') ||
      upper.startsWith('BOILER') ||
      upper.startsWith('PURIFIER-FO') ||
      upper.startsWith('PURIFIER-LO') ||
      upper === 'DECK' ||
      upper === 'IGG' ||
      upper === 'IGS' ||
      upper === 'EGCS'
    ) {
      return 'ENGDK'
    }
    if (upper === 'MARPOL') return 'MARPOL'
    if (upper === 'LSA') return 'LSA'
    if (upper === 'FFA') return 'FFA'
    if (upper === 'CARGO GEAR') return 'CGGR'
    if (upper === 'CARGO PUMPS') return 'CGOP'
    if (upper === 'MLC') return 'MLC'
    return 'OTHERS'
  }

  const usesSubHead = (equipmentName: string): boolean => {
    const upper = equipmentName.toUpperCase()
    return upper === 'MARPOL' || upper === 'LSA' || upper === 'FFA' || upper === 'OTHER MACHINERY'
  }

  const isCustomNameSelection = (value: string) => {
    return value === CUSTOM_NAME_OPTION
  }

  const handleEquipmentNameChange = (value: string) => {
    setSelectedEquipmentName(value)

    if (isCustomNameSelection(value)) {
      setSelectedEquipmentCode(CUSTOM_CODE_OPTION)
      setFormData(prev => ({
        ...prev,
        name: customEquipmentName || '',
        shortForm: generateShortForm(customEquipmentName || ''),
        code: customEquipmentCode || '',
        subHead: usesSubHead(customEquipmentName || '') ? prev.subHead || '' : '',
      }))
    } else {
      const shortForm = generateShortForm(value)
      const defaultCode = deriveDefaultCodeFromName(value)
      const resolvedCode =
        selectedEquipmentCode && selectedEquipmentCode !== CUSTOM_CODE_OPTION
          ? selectedEquipmentCode
          : (formData.code && formData.code.length > 0 ? formData.code : defaultCode)

      setSelectedEquipmentCode(resolvedCode)
      setFormData(prev => ({
        ...prev,
        name: value,
        shortForm,
        code: resolvedCode,
        subHead: usesSubHead(value) ? prev.subHead || '' : '',
      }))
    }
    if (errors.name) setErrors(prev => ({ ...prev, name: '' }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: { [key: string]: string } = {}

    const isCustomName = isCustomNameSelection(selectedEquipmentName)
    const isCustomCode = selectedEquipmentCode === CUSTOM_CODE_OPTION || isCustomName

    if (isCustomNameSelection(selectedEquipmentName)) {
      setSelectedEquipmentCode(CUSTOM_CODE_OPTION)
    }
    if (!formData.vesselId || formData.vesselId === 0) {
      newErrors.vesselId = 'Vessel is required'
    }
    if (!selectedEquipmentName || !selectedEquipmentName.trim()) {
      newErrors.name = 'Equipment name is required'
    }
    if (isCustomName && (!customEquipmentName || !customEquipmentName.trim())) {
      newErrors.name = 'Custom equipment name is required'
    }
    if (isCustomCode && (!customEquipmentCode || !customEquipmentCode.trim())) {
      newErrors.code = 'Custom equipment code is required'
    }
    const locationCode = vesselLocationCode.trim()
    if (!locationCode) {
      newErrors.vesselLocationCode = 'Location code is required'
    } else if (!LOCATION_CODE_PATTERN.test(locationCode)) {
      newErrors.vesselLocationCode = 'Format must be 00.00.00'
    }

    const finalName = isCustomName ? customEquipmentName.trim() : selectedEquipmentName.trim()
    const finalCode = isCustomCode ? customEquipmentCode.trim() : (selectedEquipmentCode || formData.code || '')
    if (!finalCode.trim()) {
      newErrors.code = 'System code is required'
    }

    let resolvedInstanceOfId: number | undefined = undefined
    if (instanceOfCode.trim()) {
      if (!allEquipment || allEquipment.length === 0) {
        newErrors.instanceOfCode = 'Instance Of requires existing equipment list'
      } else {
        const matches = allEquipment.filter(
          e =>
            e.vesselId === formData.vesselId &&
            e.code &&
            e.code.toUpperCase().trim() === instanceOfCode.trim().toUpperCase()
        )
        if (matches.length === 0) {
          newErrors.instanceOfCode = 'Instance Of system code not found for this vessel'
        } else if (matches.length > 1) {
          newErrors.instanceOfCode = 'Instance Of system code matches multiple equipments'
        } else if (editing?.id && matches[0].id === editing.id) {
          newErrors.instanceOfCode = 'Instance Of cannot reference itself'
        } else if (matches[0].id) {
          resolvedInstanceOfId = matches[0].id
        }
      }
    }

    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return

    const finalShortForm = formData.shortForm || generateShortForm(finalName)

    let resolvedLocationId = formData.vesselLocationId
    if (locationCode && formData.vesselId) {
      const existing = vesselLocations.find(
        l => l.code?.toUpperCase().trim() === locationCode.toUpperCase()
      )
      if (existing?.id) {
        resolvedLocationId = existing.id
      } else {
        try {
          const created = await createVesselLocation({
            vesselId: formData.vesselId,
            code: locationCode,
          })
          if (created?.id) {
            resolvedLocationId = created.id
            setVesselLocations(prev => [...prev, created])
          }
        } catch (error: any) {
          setErrors(prev => ({
            ...prev,
            vesselLocationCode: 'Failed to create vessel location',
          }))
          return
        }
      }
    }

    const payload: EquipmentDto = {
      ...formData,
      name: finalName,
      code: finalCode,
      vesselLocationId: resolvedLocationId,
      instanceOfEquipmentId: resolvedInstanceOfId,
      shortForm: finalShortForm,
    }

    onSubmit(payload)
  }

  if (!visible) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{ width: '720px', maxWidth: '95vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <h5 className='modal-title text-dark mx-6 mt-6'>{editing ? 'Edit Equipment' : 'Add Equipment'}</h5>
          <button type='button' className='btn-close mx-6 mt-0' onClick={onClose}></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div
            className='flex-grow-1 overflow-auto px-4 py-3 m-6'
            style={{ maxHeight: '70vh' }}
          >
            <div className='row g-3'>
              <div className='col-md-12'>
                <label className={LABEL}>
                  Vessel <span className='text-danger'>*</span>
                </label>
                <input
                  type='text'
                  className={`form-control text-dark ${errors.vesselId ? 'is-invalid' : ''}`}
                  value={selectedVesselName || 'Vessel'}
                  disabled
                />
                {errors.vesselId && <div className='invalid-feedback'>{errors.vesselId}</div>}
              </div>

              {/* Equipment Name */}
              <div className='col-md-12'>
  <label className={LABEL}>
    Equipment Name <span className='text-danger'>*</span>
  </label>
  <SearchableSelect
    className={`form-select text-dark ${errors.name ? 'is-invalid' : ''}`}
    value={selectedEquipmentName}
    onChange={(e) => handleEquipmentNameChange(e.target.value)}
  >
    <option value=''>Select Equipment Name</option>
    {EQUIPMENT_NAME_OPTIONS.map(group => (
      <optgroup key={group.group} label={group.group}>
        {group.items.map(item => {
          const isCustomOption = item === 'Other (Custom)'
          const upperItem = item.toUpperCase().trim()
          const isCurrentEditingName =
            editing && editing.name.toUpperCase().trim() === upperItem
          const isAlreadyUsed = existingNamesSet.has(upperItem)

          // Hide from dropdown if already used on THIS vessel (except the one we are editing)
          if (isAlreadyUsed && !isCurrentEditingName && !isCustomOption) {
            return null
          }

          return (
            <option key={item} value={isCustomOption ? CUSTOM_NAME_OPTION : item}>
              {item}
            </option>
          )
        })}
      </optgroup>
    ))}
  </SearchableSelect>
  {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
</div>

              {isCustomNameSelection(selectedEquipmentName) && (
                <div className='col-md-12'>
                  <label className={LABEL}>
                    Custom Equipment Name <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='text'
                    className={`form-control text-dark ${errors.name ? 'is-invalid' : ''}`}
                    value={customEquipmentName}
                    onChange={(e) => {
                      const value = e.target.value
                      setCustomEquipmentName(value)
                      setFormData(prev => ({
                        ...prev,
                        name: value,
                        shortForm: generateShortForm(value),
                      }))
                      if (errors.name) setErrors(prev => ({ ...prev, name: '' }))
                    }}
                    placeholder='Enter custom equipment name'
                  />
                </div>
              )}

              {/* System Code */}
              <div className='col-md-6'>
                <label className={LABEL}>
                  System Code <span className='text-danger'>*</span>
                </label>
                <SearchableSelect
                  className={`form-select text-dark ${errors.code ? 'is-invalid' : ''}`}
                  value={selectedEquipmentCode || ''}
                  disabled={isCustomNameSelection(selectedEquipmentName)}
                  onChange={(e) => {
                    const value = e.target.value
                    setSelectedEquipmentCode(value)
                    if (value !== CUSTOM_CODE_OPTION) {
                      setFormData(prev => ({ ...prev, code: value }))
                      if (errors.code) setErrors(prev => ({ ...prev, code: '' }))
                    }
                  }}
                >
                  <option value=''>Select Code</option>
                  {EQUIPMENT_CODE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </SearchableSelect>
              </div>

              {(selectedEquipmentCode === CUSTOM_CODE_OPTION || isCustomNameSelection(selectedEquipmentName)) && (
                <div className='col-md-6'>
                  <label className={LABEL}>
                    Custom Equipment Code <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='text'
                    className={`form-control text-dark ${errors.code ? 'is-invalid' : ''}`}
                    value={customEquipmentCode}
                    onChange={(e) => {
                      const value = e.target.value
                      setCustomEquipmentCode(value)
                      setFormData(prev => ({ ...prev, code: value }))
                      if (errors.code) setErrors(prev => ({ ...prev, code: '' }))
                    }}
                    placeholder='Enter custom equipment code'
                  />
                  {errors.code && <div className='invalid-feedback'>{errors.code}</div>}
                </div>
              )}

              {/* Short Form */}
              {/* <div className='col-md-6'>
                <label className={LABEL}>Short Form</label>
                <input
                  type='text'
                  className='form-control text-dark'
                  value={formData.shortForm || ''}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                  placeholder='Auto-generated from Equipment Name'
                />
                <div className='form-text text-muted'>Auto-generated from equipment name</div>
              </div> */}

              {/* Critical (Yes / No) */}
<div className='col-md-6 mt-3'>
  <label className={LABEL}>
    Critical Equipment
  </label>
  <div className='d-flex align-items-center gap-4 mt-1'>
    <div className='form-check form-check-inline'>
      <input
        className='form-check-input'
        type='radio'
        name='criticality'
        id='criticalYes'
        value='yes'
        checked={!!formData.criticality}
        onChange={() =>
          setFormData(prev => ({ ...prev, criticality: true }))
        }
      />
      <label className='form-check-label' htmlFor='criticalYes'>
        Yes
      </label>
    </div>

    <div className='form-check form-check-inline'>
      <input
        className='form-check-input'
        type='radio'
        name='criticality'
        id='criticalNo'
        value='no'
        checked={!formData.criticality}
        onChange={() =>
          setFormData(prev => ({ ...prev, criticality: false }))
        }
      />
      <label className='form-check-label' htmlFor='criticalNo'>
        No
      </label>
    </div>
  </div>
</div>


              {/* Sub Head */}
              {usesSubHead(selectedEquipmentName) && (
                <div className='col-md-12'>
                  <label className={LABEL}>{LABEL_HELP_SUBHEAD}</label>
                  <input
                    type='text'
                    className='form-control text-dark'
                    value={formData.subHead || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, subHead: e.target.value }))}
                    placeholder='Enter sub head or specific description'
                  />
                </div>
              )}

              {/* Maker */}
              <div className='col-md-6'>
                <label className={LABEL}>Maker</label>
                <input
                  type='text'
                  className='form-control text-dark'
                  value={formData.maker || ''}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 100)
                    setFormData(prev => ({ ...prev, maker: value }))
                  }}
                  placeholder='Equipment manufacturer'
                  maxLength={100}
                />
                <div className='form-text text-muted'>Max 100 characters</div>
              </div>

              {/* Model */}
              <div className='col-md-6'>
                <label className={LABEL}>Model</label>
                <input
                  type='text'
                  className='form-control text-dark'
                  value={formData.model || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                  placeholder='Equipment model'
                />
              </div>

              {/* System Serial Number */}
              <div className='col-md-6'>
                <label className={LABEL}>System Serial Number</label>
                <input
                  type='text'
                  className='form-control text-dark'
                  value={formData.serialNumber || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, serialNumber: e.target.value }))}
                  placeholder='Serial number'
                  maxLength={80}
                />
                <div className='form-text text-muted'>Max 80 characters</div>
              </div>

              {/* Installation Date */}
              <div className='col-md-6'>
                <label className={LABEL}>Installation Date</label>
                <input
                  type='date'
                  className='form-control text-dark'
                  value={formData.installationDate || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, installationDate: e.target.value }))}
                />
              </div>

              {/* Location Code */}
              <div className='col-md-12'>
                <label className={LABEL}>
                  Location Code <span className='text-danger'>*</span>
                </label>
                <input
                  type='text'
                  className={`form-control text-dark ${errors.vesselLocationCode ? 'is-invalid' : ''}`}
                  value={vesselLocationCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase()
                    setVesselLocationCode(value)
                    setFormData(prev => ({ ...prev, vesselLocationId: undefined }))
                    if (errors.vesselLocationCode) {
                      setErrors(prev => ({ ...prev, vesselLocationCode: '' }))
                    }
                  }}
                  placeholder='e.g., 00.00.00'
                />
                {errors.vesselLocationCode && (
                  <div className='invalid-feedback'>{errors.vesselLocationCode}</div>
                )}
                <div className='form-text text-muted'>Format: 00.00.00</div>
              </div>

              {/* Equipment Function Description */}
              <div className='col-md-12'>
                <label className={LABEL}>System Description</label>
                <input
                  type='text'
                  className='form-control text-dark'
                  value={formData.equipmentFunctionDescription || ''}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 150)
                    setFormData(prev => ({ ...prev, equipmentFunctionDescription: value }))
                  }}
                  placeholder='e.g., MAIN PROPULSION & ACCESSORIES'
                  maxLength={150}
                />
                <div className='form-text text-muted'>Max 150 characters</div>
              </div>

              {/* System Particulars */}
              <div className='col-md-12'>
                <label className={LABEL}>System Particulars</label>
                <textarea
                  className='form-control text-dark'
                  value={formData.equipmentParticulars || ''}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 2000)
                    setFormData(prev => ({ ...prev, equipmentParticulars: value }))
                  }}
                  placeholder='System particulars'
                  rows={3}
                  maxLength={2000}
                />
                <div className='form-text text-muted'>Max 2000 characters</div>
              </div>

              {/* Location Comments */}
              <div className='col-md-12'>
                <label className={LABEL}>Location Comments</label>
                <textarea
                  className='form-control text-dark'
                  value={formData.vesselLocationComments || ''}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 2000)
                    setFormData(prev => ({ ...prev, vesselLocationComments: value }))
                  }}
                  placeholder='Location comments'
                  rows={2}
                  maxLength={2000}
                />
                <div className='form-text text-muted'>Max 2000 characters</div>
              </div>

              {/* Instance Of */}
              <div className='col-md-6'>
                <label className={LABEL}>Instance Of (System Code)</label>
                <input
                  type='text'
                  className={`form-control text-dark ${errors.instanceOfCode ? 'is-invalid' : ''}`}
                  value={instanceOfCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase()
                    setInstanceOfCode(value)
                    if (errors.instanceOfCode) setErrors(prev => ({ ...prev, instanceOfCode: '' }))
                  }}
                  placeholder='Optional'
                />
                {errors.instanceOfCode && <div className='invalid-feedback'>{errors.instanceOfCode}</div>}
              </div>

            </div>
          </div>

          <div className='border-top d-flex justify-content-end gap-2 px-4 py-3 flex-shrink-0 m-6'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              Cancel
            </button>
            <button type='submit' className='btn btn-primary btn-sm'>
              {editing ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
